import { useCallback, useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import ReservationSettings from './ReservationSettings';

const emptyService = { id: '', name: '', description: '', base_price: '', category_id: '', locations: '', rates: {}, track_flights: false };
const money = (value) => Number.parseFloat(value || 0);

function ServicesSettingsView({ orgId, userId, theme = 'light' }) {
    const dark = theme === 'dark';
    const [services, setServices] = useState([]);
    const [categories, setCategories] = useState([]);
    const [zones, setZones] = useState([]);
    const [locations, setLocations] = useState([]);
    const [rates, setRates] = useState([]);
    const [serviceForm, setServiceForm] = useState(emptyService);
    const [categoryForm, setCategoryForm] = useState({ id: '', name: '', description: '' });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    // FA-2: "Track flights" per service. Until migration 237 is applied the column is not there, so the catalog loads without it.
    const [canTrackFlights, setCanTrackFlights] = useState(true);

    const loadCatalog = useCallback(async () => {
        if (!orgId) {
            setLoading(false);
            setError('No organization is available for the service catalog.');
            return;
        }
        setLoading(true);
        setError('');
        const serviceQuery = (columns) => supabaseClient.from('services').select(columns).eq('org_id', orgId).order('name');
        const [firstServiceResult, categoryResult, zoneResult, locationResult, rateResult] = await Promise.all([
            serviceQuery('id, name, description, base_price, is_active, category_id, track_flights'),
            supabaseClient.from('service_categories').select('id, name, description, is_active').eq('org_id', orgId).order('name'),
            supabaseClient.from('zones').select('id, name').eq('org_id', orgId).order('display_order').order('name'),
            supabaseClient.from('service_fixed_locations').select('id, service_id, label, address, is_active').eq('org_id', orgId),
            supabaseClient.from('service_zone_rates').select('id, service_id, zone_id, rate').eq('org_id', orgId),
        ]);
        let serviceResult = firstServiceResult;
        const trackColumnMissing = /track_flights/.test(firstServiceResult.error?.message || '');
        if (trackColumnMissing) serviceResult = await serviceQuery('id, name, description, base_price, is_active, category_id');
        setCanTrackFlights(!trackColumnMissing);
        const failed = [serviceResult, categoryResult, zoneResult, locationResult, rateResult].find((result) => result.error);
        if (failed) {
            setError(failed.error.message || 'Unable to load the service catalog.');
        } else {
            setServices(serviceResult.data || []);
            setCategories(categoryResult.data || []);
            setZones(zoneResult.data || []);
            setLocations(locationResult.data || []);
            setRates(rateResult.data || []);
        }
        setLoading(false);
    }, [orgId]);

    useEffect(() => { loadCatalog(); }, [loadCatalog]);

    const editService = (service) => {
        const serviceLocations = locations
            .filter((location) => location.service_id === service.id && location.is_active)
            .map((location) => `${location.label}|${location.address}`)
            .join('\n');
        const serviceRates = Object.fromEntries(rates
            .filter((rate) => rate.service_id === service.id)
            .map((rate) => [rate.zone_id, String(rate.rate)]));
        setServiceForm({
            id: service.id,
            name: service.name,
            description: service.description || '',
            base_price: String(service.base_price ?? ''),
            category_id: service.category_id || '',
            locations: serviceLocations,
            rates: serviceRates,
            track_flights: Boolean(service.track_flights),
        });
        setNotice('');
        setError('');
    };

    const saveService = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError('');
        setNotice('');
        try {
            const fields = {
                name: serviceForm.name.trim(),
                description: serviceForm.description.trim() || null,
                base_price: money(serviceForm.base_price),
                category_id: serviceForm.category_id || null,
                ...(canTrackFlights ? { track_flights: Boolean(serviceForm.track_flights) } : {}),
            };
            if (!fields.name) throw new Error('Service name is required.');
            if (!Number.isFinite(fields.base_price) || fields.base_price < 0) throw new Error('Base price must be zero or more.');

            let serviceId = serviceForm.id;
            if (serviceId) {
                const { error: updateError } = await supabaseClient.from('services')
                    .update(fields).eq('id', serviceId).eq('org_id', orgId);
                if (updateError) throw updateError;
            } else {
                const { data, error: insertError } = await supabaseClient.from('services')
                    .insert([{ ...fields, org_id: orgId, created_by: userId }])
                    .select('id')
                    .single();
                if (insertError) throw insertError;
                serviceId = data.id;
            }

            const parsedLocations = serviceForm.locations.split('\n').map((line) => line.trim()).filter(Boolean).map((line) => {
                const separator = line.indexOf('|');
                if (separator < 1 || separator === line.length - 1) throw new Error('Enter each fixed destination as Label|Address.');
                return { label: line.slice(0, separator).trim(), address: line.slice(separator + 1).trim() };
            });
            const { error: locationsError } = await supabaseClient.from('service_fixed_locations')
                .update({ is_active: false, updated_at: new Date().toISOString() })
                .eq('service_id', serviceId).eq('org_id', orgId);
            if (locationsError) throw locationsError;
            if (parsedLocations.length) {
                const { error: upsertLocationsError } = await supabaseClient.from('service_fixed_locations').upsert(
                    parsedLocations.map((location) => ({ ...location, service_id: serviceId, org_id: orgId, created_by: userId, is_active: true })),
                    { onConflict: 'service_id,label,address' }
                );
                if (upsertLocationsError) throw upsertLocationsError;
            }

            const rateRows = zones.map((zone) => {
                const value = serviceForm.rates[zone.id];
                return value === undefined || value === ''
                    ? null
                    : { service_id: serviceId, zone_id: zone.id, org_id: orgId, created_by: userId, rate: money(value) };
            }).filter(Boolean);
            if (rateRows.some((row) => !Number.isFinite(row.rate) || row.rate < 0)) throw new Error('Zone rates must be zero or more.');
            if (rateRows.length) {
                const { error: rateError } = await supabaseClient.from('service_zone_rates').upsert(rateRows, { onConflict: 'service_id,zone_id' });
                if (rateError) throw rateError;
            }
            setServiceForm(emptyService);
            setNotice('Service saved.');
            await loadCatalog();
        } catch (saveError) {
            setError(saveError.message || 'Unable to save service.');
        } finally {
            setSaving(false);
        }
    };

    const archiveService = async (service) => {
        setError('');
        const { error: archiveError } = await supabaseClient.from('services')
            .update({ is_active: false }).eq('id', service.id).eq('org_id', orgId);
        if (archiveError) setError(archiveError.message || 'Unable to archive service.');
        else {
            setNotice('Service archived.');
            await loadCatalog();
        }
    };

    const saveCategory = async (event) => {
        event.preventDefault();
        setError('');
        setNotice('');
        const fields = { name: categoryForm.name.trim(), description: categoryForm.description.trim() || null };
        if (!fields.name) {
            setError('Category name is required.');
            return;
        }
        const result = categoryForm.id
            ? await supabaseClient.from('service_categories').update(fields).eq('id', categoryForm.id).eq('org_id', orgId)
            : await supabaseClient.from('service_categories').insert([{ ...fields, org_id: orgId }]);
        if (result.error) setError(result.error.message || 'Unable to save category.');
        else {
            setCategoryForm({ id: '', name: '', description: '' });
            setNotice('Category saved.');
            await loadCatalog();
        }
    };

    const archiveCategory = async (category) => {
        const { error: archiveError } = await supabaseClient.from('service_categories')
            .update({ is_active: false }).eq('id', category.id).eq('org_id', orgId);
        if (archiveError) setError(archiveError.message || 'Unable to archive category.');
        else {
            setNotice('Category archived.');
            await loadCatalog();
        }
    };

    const surface = dark ? 'border-gray-700 bg-gray-800 text-gray-100' : 'border-gray-200 bg-white text-gray-900';
    const field = `w-full rounded-lg border px-3 py-2 ${dark ? 'border-gray-600 bg-gray-700 text-gray-100' : 'border-gray-300 bg-white text-gray-900'}`;

    return (
        <main className="mx-auto max-w-5xl space-y-6 p-4" data-theme={theme}>
            <header>
                <h1 className="text-2xl font-bold text-[var(--ink)]">Services</h1>
                <p className="mt-1 text-sm text-[var(--ink-muted)]">Manage service types, starting fares, zone rates, and fixed destinations.</p>
            </header>
            <ReservationSettings client={supabaseClient} orgId={orgId} />
            {error && <div role="alert" className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
            {notice && <div role="status" className="rounded-lg border border-green-300 bg-green-50 p-3 text-sm text-green-800">{notice}</div>}
            {loading ? <p role="status">Loading services…</p> : (
                <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
                    <section className={`rounded-xl border p-5 ${surface}`}>
                        <h2 className="mb-4 text-lg font-semibold">{serviceForm.id ? 'Edit service' : 'Add service'}</h2>
                        <form onSubmit={saveService} className="space-y-4">
                            <label className="block text-sm font-medium">Service name
                                <input className={`${field} mt-1`} value={serviceForm.name} onChange={(event) => setServiceForm({ ...serviceForm, name: event.target.value })} required />
                            </label>
                            <label className="block text-sm font-medium">Description
                                <textarea className={`${field} mt-1`} rows="2" value={serviceForm.description} onChange={(event) => setServiceForm({ ...serviceForm, description: event.target.value })} />
                            </label>
                            <div className="grid gap-4 sm:grid-cols-2">
                                <label className="block text-sm font-medium">Base fare
                                    <input className={`${field} mt-1`} type="number" min="0" step="0.01" value={serviceForm.base_price} onChange={(event) => setServiceForm({ ...serviceForm, base_price: event.target.value })} required />
                                </label>
                                <label className="block text-sm font-medium">Category
                                    <select className={`${field} mt-1`} value={serviceForm.category_id} onChange={(event) => setServiceForm({ ...serviceForm, category_id: event.target.value })}>
                                        <option value="">No category</option>
                                        {categories.filter((category) => category.is_active).map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
                                    </select>
                                </label>
                            </div>
                            {canTrackFlights && (
                                <label className="flex items-start gap-2 text-sm font-medium">
                                    <input type="checkbox" className="mt-1" checked={Boolean(serviceForm.track_flights)}
                                        onChange={(event) => setServiceForm({ ...serviceForm, track_flights: event.target.checked })} />
                                    <span>Track flights
                                        <span className="block font-normal text-[var(--ink-muted)]">Airport trips on this service follow their flight with FlightAware (a paid service, capped per month in Admin Center).</span>
                                    </span>
                                </label>
                            )}
                            <label className="block text-sm font-medium">Fixed dropoff locations <span className="font-normal text-[var(--ink-muted)]">(one `Label|Address` per line)</span>
                                <textarea className={`${field} mt-1`} rows="3" value={serviceForm.locations} onChange={(event) => setServiceForm({ ...serviceForm, locations: event.target.value })} placeholder="Airport|Hartsfield-Jackson Atlanta International Airport" />
                            </label>
                            {zones.length > 0 && (
                                <fieldset className="space-y-2">
                                    <legend className="text-sm font-medium">Additional fare by zone</legend>
                                    {zones.map((zone) => <label key={zone.id} className="grid grid-cols-[1fr_10rem] items-center gap-3 text-sm">
                                        <span>{zone.name}</span>
                                        <input className={field} type="number" min="0" step="0.01" aria-label={`${zone.name} additional fare`} value={serviceForm.rates[zone.id] || ''} onChange={(event) => setServiceForm({ ...serviceForm, rates: { ...serviceForm.rates, [zone.id]: event.target.value } })} placeholder="No rate" />
                                    </label>)}
                                </fieldset>
                            )}
                            {zones.length === 0 && <p className="text-sm text-[var(--ink-muted)]">No zones are configured; fare uses the base price only.</p>}
                            <div className="flex gap-2">
                                <button type="submit" disabled={saving} className="rounded-lg bg-orange-600 px-4 py-2 font-medium text-white disabled:opacity-50">{saving ? 'Saving…' : 'Save service'}</button>
                                {serviceForm.id && <button type="button" className="rounded-lg border px-4 py-2" onClick={() => setServiceForm(emptyService)}>Cancel edit</button>}
                            </div>
                        </form>
                        <div className="mt-6 border-t border-[var(--surface-border)] pt-4">
                            <h3 className="mb-3 font-semibold">Service catalog</h3>
                            {services.length === 0 ? <p className="text-sm text-[var(--ink-muted)]">No services yet.</p> : (
                                <ul className="divide-y divide-[var(--surface-border)]">
                                    {services.map((service) => <li key={service.id} className="flex items-center justify-between gap-4 py-3">
                                        <div>
                                            <p className="font-medium">{service.name} <span className="text-sm font-normal text-[var(--ink-muted)]">${money(service.base_price).toFixed(2)} base</span></p>
                                            <p className="text-sm text-[var(--ink-muted)]">{categories.find((category) => category.id === service.category_id)?.name || 'Uncategorized'}{service.track_flights ? ' · Tracks flights' : ''}{service.is_active ? '' : ' · Archived'}</p>
                                        </div>
                                        <div className="flex gap-3 text-sm">
                                            <button type="button" className="text-[var(--accent-ink)] underline" onClick={() => editService(service)}>Edit</button>
                                            {service.is_active && <button type="button" className="text-red-600 underline" onClick={() => archiveService(service)}>Archive</button>}
                                        </div>
                                    </li>)}
                                </ul>
                            )}
                        </div>
                    </section>
                    <section className={`rounded-xl border p-5 ${surface}`}>
                        <h2 className="mb-4 text-lg font-semibold">{categoryForm.id ? 'Edit category' : 'Service categories'}</h2>
                        <form onSubmit={saveCategory} className="space-y-3">
                            <label className="block text-sm font-medium">Category name
                                <input className={`${field} mt-1`} value={categoryForm.name} onChange={(event) => setCategoryForm({ ...categoryForm, name: event.target.value })} required />
                            </label>
                            <label className="block text-sm font-medium">Description
                                <textarea className={`${field} mt-1`} rows="2" value={categoryForm.description} onChange={(event) => setCategoryForm({ ...categoryForm, description: event.target.value })} />
                            </label>
                            <button type="submit" className="rounded-lg bg-orange-600 px-4 py-2 font-medium text-white">Save category</button>
                        </form>
                        <ul className="mt-5 divide-y divide-[var(--surface-border)]">
                            {categories.map((category) => <li key={category.id} className="flex items-center justify-between gap-3 py-3 text-sm">
                                <span>{category.name}{category.is_active ? '' : ' · Archived'}</span>
                                <span className="flex gap-3">
                                    <button type="button" className="text-[var(--accent-ink)] underline" onClick={() => setCategoryForm({ id: category.id, name: category.name, description: category.description || '' })}>Edit</button>
                                    {category.is_active && <button type="button" className="text-red-600 underline" onClick={() => archiveCategory(category)}>Archive</button>}
                                </span>
                            </li>)}
                        </ul>
                    </section>
                </div>
            )}
        </main>
    );
}

export default ServicesSettingsView;
